import { Fragment, useState } from 'react';
import { cn } from 'Utils/cn';
import PropTypes from 'prop-types';
import Tooltip from 'Components/Molecules/Tooltip/Tooltip';
import {
    useFloating,
    useClick,
    useDismiss,
    useRole,
    useInteractions,
    FloatingFocusManager,
    FloatingPortal,
    offset,
    flip,
    size,
    autoUpdate,
} from '@floating-ui/react';
import Icon from 'Components/Atoms/Icon/Icon';
import { AnimatePresence, motion } from 'framer-motion';

export default function ActionsMenu({ className, actions, icon, disablePortal }) {
    const [isOpen, setIsOpen] = useState(false);

    const { refs, floatingStyles, context, strategy, x, y } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        whileElementsMounted: autoUpdate,
        strategy: 'absolute',
        placement: 'bottom-end',
        middleware: [
            offset(4),
            flip(),
            size({
                apply({ elements, availableHeight }) {
                    Object.assign(elements.floating.style, {
                        maxHeight: `${Math.min(availableHeight - 20, 520)}px`,
                    });
                },
            }),
        ],
    });

    const { getFloatingProps, getReferenceProps } = useInteractions([
        useClick(context),
        useRole(context, { role: 'listbox' }),
        useDismiss(context),
    ]);

    const Wrapper = disablePortal ? Fragment : FloatingPortal;
    return (
        <>
            <button
                ref={(node) => {
                    refs.setReference(node);
                }}
                type="button"
                className={cn(
                    'inline-flex items-center text-dark-blue/50 border border-transparent',
                    'rounded-md focus-visible:outline-none focus-visible:ring-2 ring-primary/20 focus-visible:border-primary transition-all',
                    'shrink-0',
                    className,
                )}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                {...getReferenceProps()}
            >
                {icon ? icon : <Icon name="dots-vertical" size={20} color="currentColor" />}
            </button>

            <Wrapper>
                <AnimatePresence>
                    {isOpen && (
                        <FloatingFocusManager context={context}>
                            <motion.div
                                ref={refs.setFloating}
                                style={{
                                    ...floatingStyles,
                                    position: strategy,
                                    top: y ?? 0,
                                    left: x ?? 0,
                                }}
                                className="mt-1 overflow-x-hidden overflow-y-auto scrollbar-thin rounded-md bg-white shadow-sm ring-1 ring-black/5 focus:outline-none text-sm min-w-[150px] z-dropdown"
                                tabIndex={-1}
                                {...getFloatingProps()}
                                initial={{ opacity: 0, y: -8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }}
                                transition={{ duration: 0.15, ease: 'easeOut' }}
                            >
                                <ul className="p-1.5">
                                    {actions.map((option, index) => {
                                        const button = (
                                            <button
                                                type="button"
                                                disabled={option.disabled}
                                                onClick={() => {
                                                    option.onClick();
                                                    setIsOpen(false);
                                                }}
                                                className={cn(
                                                    'w-full cursor-pointer flex gap-2 px-3 py-2 rounded-sm transition-colors hover:bg-gray-100',
                                                    'disabled:cursor-not-allowed disabled:text-gray-400 hover:disabled:bg-transparent',
                                                    option.className,
                                                )}
                                            >
                                                {!!option.icon && (
                                                    <span className="inline-flex shrink-0">{option.icon}</span>
                                                )}
                                                {option.label}
                                            </button>
                                        );
                                        return (
                                            <li key={index}>
                                                {option.disabled && option.disabledReason ? (
                                                    <Tooltip label={option.disabledReason}>
                                                        <span className="block w-full">{button}</span>
                                                    </Tooltip>
                                                ) : (
                                                    button
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </motion.div>
                        </FloatingFocusManager>
                    )}
                </AnimatePresence>
            </Wrapper>
        </>
    );
}

ActionsMenu.propTypes = {
    className: PropTypes.string,
    actions: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.node.isRequired,
            onClick: PropTypes.func.isRequired,
            icon: PropTypes.node,
            className: PropTypes.string,
            disabled: PropTypes.bool,
            disabledReason: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
        }),
    ).isRequired,
    icon: PropTypes.node,
    disablePortal: PropTypes.bool,
};
